import { Button, DataTable, FlexLayout, Icon } from '@deity-io/portal-ui';
import { BaseHelmet, ConfirmationModal, CreateEditStructureModal } from 'components';
import { ValidationStatusBadge } from 'components/Flows/ValidationStatusBadge';
import { APOLLO_BASE_OPTIONS, ENTITY_PATH, ENTITY_PATH_ID, toLocalDateString } from 'contracts/constants';
import { DEFAULT_PAGINATION_SIZE } from 'contracts/constants/page-title';
import { EntityWrapper } from 'layouts';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  GetModelsByPackageVersionDocument,
  useDeleteModelMutation,
  useGetModelsByPackageVersionLazyQuery
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import { v4 } from 'uuid';

interface StructuresProps {
  isEditable: boolean;
}
const Structures: FC<StructuresProps> = ({ isEditable }) => {
  const history = useHistory();
  const [paging, setPaging] = useState({ page: 1, pageSize: DEFAULT_PAGINATION_SIZE });
  const { url } = useRouteMatch();
  const { pathname } = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [structureToDelete, setStructureToDelete] = useState<any>();
  const [getAllModels, { loading, data: models, fetchMore }] =
    useGetModelsByPackageVersionLazyQuery(APOLLO_BASE_OPTIONS);
  const params: any = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const totalItems = models?.getModelsByPackageVersion?.totalItems;
  const data = models?.getModelsByPackageVersion?.data;
  const [newStructureKey, setNewStructureKey] = useState(v4());

  const [deleteStructure] = useDeleteModelMutation({
    refetchQueries: [
      {
        query: GetModelsByPackageVersionDocument,
        variables: {
          packageVersionUuid: params?.versionUuid,
          offset: 0,
          limit: models?.getModelsByPackageVersion?.data.length || 25
        }
      }
    ]
  });

  useEffect(() => {
    if (params?.versionUuid) {
      getAllModels({
        variables: {
          offset: (paging.page - 1) * paging.pageSize,
          limit: paging.pageSize,
          packageVersionUuid: params.versionUuid
        }
      });
    }
  }, [pathname, paging]);

  // eslint-disable-next-line react/no-unstable-nested-components
  const NewStructureButton = () => (
    <Button
      variant="outlined"
      disabled={isModalOpen}
      onClick={() => setIsModalOpen(true)}
      startIcon={<Icon icon="add" />}
    >
      New Structure
    </Button>
  );

  return (
    <>
      <BaseHelmet page="Structures" />

      <EntityWrapper title="Structures" button={<NewStructureButton />}>
        <DataTable
          data={data || []}
          columns={[
            {
              key: 'name',
              name: 'STRUCTURE',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: ({ value }) => <b>{value}</b>,
              width: 40
            },
            {
              key: 'validation',
              name: 'STATUS',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: validation => <ValidationStatusBadge validation={validation.value} />
            },
            {
              key: 'modifiedAt',
              name: 'LAST MODIFIED',
              formatter: ({ value }) => toLocalDateString(value!)
            },
            {
              key: 'modifiedBy',
              name: 'LAST MODIFIED BY',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: () => <div>John Smith</div>
            },
            {
              key: 'uuid',
              name: 'ACTIONS',
              align: 'left',
              isActionCell: true,
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: ({ value }) => (
                <FlexLayout>
                  <Button endIcon={<Icon icon="visibility" />} onClick={() => history.push(`${url}/${value}`)} />

                  {isEditable && (
                    <Button
                      endIcon={<Icon icon="delete" color="error" />}
                      onClick={() => {
                        setStructureToDelete(value);
                        setIsDeleteModalOpen(true);
                      }}
                    />
                  )}
                </FlexLayout>
              )
            }
          ]}
          pageSize={paging.pageSize}
          onPageSizeChange={size => {
            setPaging({ ...paging, pageSize: size });
          }}
          totalResults={totalItems || 0}
          page={paging.page}
          showPagination
          isLoading={loading}
          onPageChange={pageNr => {
            setPaging({ ...paging, page: pageNr });
          }}
          onRowClick={endpoint => history.push(`${url}/${endpoint.uuid}`)}
          fullClickable
          noResultsMsg="No structures available for this version"
        />
      </EntityWrapper>

      <CreateEditStructureModal
        key={newStructureKey}
        isOpen={isModalOpen}
        versionUuid={params?.versionUuid}
        setOpenState={setIsModalOpen}
        onPackageCreated={() => {
          setNewStructureKey(v4());
          setPaging({ ...paging, page: 1 });
        }}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        entityType="structure"
        entityUuid={structureToDelete}
        onConfirm={uuid => deleteStructure({ variables: { packageVersionUuid: params.versionUuid, uuid: uuid! } })}
      />
    </>
  );
};

export default Structures;
